import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Grid, Link as LinkMaterial } from '@material-ui/core';
import { FormSubtypeEnum } from '@dev/ee-api/ee-api-models';
import { BasicInput, CollapsableRows, CollapsableRowsRowProps, CopyButton, CopyButtonVariant, InfoIcon, TertiaryButton, TextArea, Tooltip, Typography } from '@dev/ee-components';
import { HotJarEvents, useHotJar } from '@/services/hooks/useHotjar';
import { getEmbeddedForm } from '../../tools/getEmbeddedForm';
import { StepPublishContentProps } from './types';
import { ContentWrapper, OpenInNewIconBlue, IconWrapper } from './styles';

export const StepPublishContent = ({ formObject, isStepActive }: StepPublishContentProps): JSX.Element => {
    const { t } = useTranslation();
    const { PublicFormID, Link } = formObject || {};
    const embeddedFormHtml = getEmbeddedForm(formObject);

    const { showSurvey } = useHotJar();

    useEffect(()=>{
        isStepActive && showSurvey(HotJarEvents.PublishForm)
    },[isStepActive])

    const formLinkButtonText = {
        copy: t('formlinkbuttoncopy'),
        copied: t('formlinkbuttoncopied'),
    };
    const isPopup = formObject?.FormSubtype === FormSubtypeEnum.Popup;

    const formLinkRows: CollapsableRowsRowProps[] = [
        {
            headerText: {
                text: t('formlink'),
            },
            isVerified: false,
            content:
                <ContentWrapper>
                    <Grid container xs={12}>
                        <Grid xs={12} >
                            <Typography variant='body1'>{t('formlinkdescription')}</Typography>
                        </Grid>
                        <Grid xs={12} style={{ paddingTop: 16 }}>
                            <BasicInput value={Link} />
                        </Grid>
                        <Grid xs={12} style={{ paddingTop: 4, marginLeft: 2 }}>
                            <LinkMaterial
                                target="_blank"
                                href={Link}
                            >
                                <TertiaryButton text={t('formlinkpreviewbutton')} startIcon={<OpenInNewIconBlue />} />
                            </LinkMaterial>
                        </Grid>
                        <Grid xs={12} style={{ paddingTop: 24 }}>
                            <CopyButton
                                text={formLinkButtonText}
                                variant={CopyButtonVariant.Website}
                                valueToCopy={Link}
                                disableSentenceCase
                            />
                        </Grid>
                    </Grid>
                </ContentWrapper>,
            expanded: !isPopup,
        }
    ];

    const simpleEmbedButtonText = {
        copy: t('copyhtmlcode'),
        copied: t('copiedhtmlcode'),
    };

    const simpleEmbedRows: CollapsableRowsRowProps[] = [
        {
            headerText: {
                text: t('simpleembedhtml'),
            },
            isVerified: false,
            content:
                <ContentWrapper>
                    <Grid container xs={12}>
                        <Grid xs={12} >
                            <Typography variant='body1'>{t('simpleembedhtmldescription')}</Typography>
                        </Grid>
                        <Grid xs={12} style={{ paddingTop: 16 }}>
                            <TextArea id="simple-embed-html-text-area" rowsMax={3} value={embeddedFormHtml} />
                        </Grid>
                        <Grid xs={12}>
                            <CopyButton
                                text={simpleEmbedButtonText}
                                variant={CopyButtonVariant.Website}
                                valueToCopy={embeddedFormHtml}
                                disableSentenceCase
                            />
                        </Grid>
                    </Grid>
                </ContentWrapper>,
            expanded: isPopup,
        }
    ];

    const rows = isPopup ? [simpleEmbedRows, formLinkRows] : [formLinkRows, simpleEmbedRows];

    return (
        <Grid container xs={12} justifyContent="center">
            <Grid xs={12} >
                <CollapsableRows rows={rows[0]} isVerifiedIconHide={true} />
            </Grid>
            <Grid xs={12} style={{ paddingTop: 24 }} >
                <CollapsableRows rows={rows[1]} isVerifiedIconHide={true} />
            </Grid>
            <Grid container alignItems="center" xs={12} style={{ paddingTop: 24 }} >
                <Typography variant='h4'>{t('publicformid')}</Typography>
                <Tooltip id="public-form-id-tooltip" title={t('publicformidtooltipcontent')}>
                    <IconWrapper>
                        <InfoIcon />
                    </IconWrapper>
                </Tooltip>
                <Typography variant='body1'>{PublicFormID}</Typography>
            </Grid>
        </Grid>
    );
}
